/**
 * COMPONENT: BedOptionsStep
 * Étape ⑤ — options et consentements (cahier L88-91).
 *
 * - les options FACULTATIVES sont séparées visuellement des montants
 *   OBLIGATOIRES (« il faut séparer la partie Options qui est facultative des
 *   parties obligatoires ») ;
 * - chaque ligne peut porter un LIEN vers son document de conditions et, dans
 *   ce cas, une case « j'ai pris connaissance » OBLIGATOIRE — pour une option,
 *   elle conditionne la possibilité même de la cocher (exigence de l'assureur,
 *   cahier L91) ; pour un item obligatoire, elle doit être cochée avant de
 *   poursuivre ;
 * - les acquittements sont persistés par le socle 00166
 *   (`payment_case_acknowledgments`) à la soumission, avec l'URL AFFICHÉE en
 *   écho — c'est ce qui rend la preuve opposable.
 */

import { ExternalLink, Lock } from 'lucide-react';
import type { BedPricingItem, BedResumePaymentState } from '../../lib/bed/draft';
import { BedAmountsTable } from './BedAmountsTable';
import type { BedRecapAmounts } from './BedRecapStep';
import { formatCents, isConditionsAcknowledged } from '../../lib/bed/wizard-model';
import type { FormFieldWithValues } from '../../types/form';
import { BedFieldInput } from './BedFieldInput';
import type { BedValues } from '../../lib/bed/wizard-model';

interface BedOptionsStepProps {
  mandatoryItems: BedPricingItem[];
  optionalItems: BedPricingItem[];
  selectedIds: string[];
  /** `{ [pricing_item_id]: conditions_url acquittée }`. */
  acknowledged: Record<string, string>;
  onToggleOption: (item: BedPricingItem, selected: boolean) => void;
  onToggleAck: (item: BedPricingItem, acknowledged: boolean) => void;
  /** Champs de consentement (règlement, presse, prestataires) de l'étape ⑤. */
  consentFields: FormFieldWithValues[];
  values: BedValues;
  isRequired: (field: FormFieldWithValues) => boolean;
  onChangeField: (fieldId: string, value: string) => void;
  disabled?: boolean;
  /**
   * Migration 00249 — le paiement du dossier a commencé : le socle 00166 refuse
   * toute nouvelle sélection d'options. Prop DÉDIÉE et non `disabled` global :
   * les consentements de l'étape (règlement, presse, prestataires) et les
   * prises de connaissance doivent, eux, rester éditables.
   */
  optionsLocked?: boolean;
  /**
   * Migration 00280 — PREMIER envoi sur un dossier déjà engagé par le
   * participant : ids des options déjà au dossier (cochées, non retirables).
   * Aucune autre ne peut être ajoutée ; une option cochée avant l'engagement
   * reste décochable. `null` = rien de figé.
   */
  engagedOptionIds?: string[] | null;
  /** 00252 (retours 19/23) : montants serveur pour le récapitulatif vivant. */
  amounts?: BedRecapAmounts | null;
  selectedOptions?: BedPricingItem[];
  totalCents?: number;
  /** 00252 (retour 21) : date butoir de paiement du solde. */
  balanceDeadline?: string | null;
  /** 00240 : dossier de paiement existant (réédition) — son restant dû fait foi. */
  resumeState?: BedResumePaymentState | null;
  /** Chambre double à Marrakech — choix obligatoire. */
  doubleRoomAnswer?: 'oui' | 'non' | null;
  onDoubleRoomChange?: (v: 'oui' | 'non') => void;
  /** 00296 : solde couvert sans dossier, aucune option ne peut être prise en ligne. */
  optionsUnavailable?: boolean;
}

function ConditionsLink({ url }: { url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-sm font-medium text-zinc-900 underline underline-offset-2 hover:text-zinc-700"
    >
      Consulter le document
      <ExternalLink className="h-3.5 w-3.5" />
    </a>
  );
}

export function BedOptionsStep({
  mandatoryItems,
  optionalItems,
  selectedIds,
  acknowledged,
  onToggleOption,
  onToggleAck,
  consentFields,
  values,
  isRequired,
  onChangeField,
  disabled = false,
  optionsLocked = false,
  engagedOptionIds = null,
  amounts = null,
  selectedOptions = [],
  totalCents = 0,
  balanceDeadline = null,
  resumeState = null,
  doubleRoomAnswer = null,
  onDoubleRoomChange,
  optionsUnavailable = false,
}: BedOptionsStepProps) {
  return (
    <div className="space-y-8">
      {/* ---------------- 00252 (retours 19/23) : récapitulatif VIVANT ---------------- */}
      <section className="space-y-3">
        <div>
          <h3 className="text-base font-semibold text-zinc-900">Votre engagement</h3>
          <p className="text-sm text-zinc-600">
            {optionsUnavailable
              ? 'Vous retrouverez ce détail au récapitulatif.'
              : 'Le détail ci-dessous se met à jour au fil des options que vous choisissez ; vous le retrouverez, figé, au récapitulatif.'}
          </p>
        </div>
        <BedAmountsTable
          amounts={amounts}
          mandatoryItems={mandatoryItems}
          selectedOptions={selectedOptions}
          totalCents={totalCents}
          variant="live"
          balanceDeadline={balanceDeadline}
          resumeState={resumeState}
          optionsUnavailable={optionsUnavailable}
          testId="bed-options-recap"
        />
      </section>

      {/* ---------------- Montants obligatoires ---------------- */}
      <section className="space-y-3">
        <div>
          <h3 className="text-base font-semibold text-zinc-900">Inclus dans votre engagement</h3>
          <p className="text-sm text-zinc-600">
            Ces montants sont obligatoires : ils font partie de votre inscription.
          </p>
        </div>

        <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200">
          {mandatoryItems.map((item) => {
            const acked = isConditionsAcknowledged(item, acknowledged);
            return (
              <li key={item.pricing_item_id} className="space-y-2 p-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="text-sm font-medium text-zinc-900">{item.label}</span>
                  <span className="text-sm font-semibold text-zinc-900">
                    {formatCents(item.amount_cents)}
                  </span>
                </div>

                {item.conditions_url && (
                  <div className="space-y-2 rounded-md bg-zinc-50 p-3">
                    <ConditionsLink url={item.conditions_url} />
                    <div className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        id={`ack-${item.pricing_item_id}`}
                        checked={acked}
                        disabled={disabled}
                        onChange={(event) => onToggleAck(item, event.target.checked)}
                        className="mt-0.5 h-4 w-4 shrink-0 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900"
                      />
                      <label
                        htmlFor={`ack-${item.pricing_item_id}`}
                        className="cursor-pointer text-sm text-zinc-700"
                      >
                        J’ai pris connaissance du document
                        <span className="ml-1 text-red-500">*</span>
                      </label>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
          {mandatoryItems.length === 0 && (
            <li className="p-4 text-sm text-zinc-500">Aucun montant obligatoire configuré.</li>
          )}
        </ul>
      </section>

      {/* ---------------- Options facultatives ---------------- */}
      {/* 00296 : solde couvert sans dossier, aucune option en ligne (le
          bandeau du wizard le dit) : on ne propose rien à cocher. */}
      {!optionsUnavailable && (
        <section className="space-y-3">
          <div>
            <h3 className="text-base font-semibold text-zinc-900">Options</h3>
            <p className="text-sm text-zinc-600">
              Libre à vous de les ajouter — elles s’ajoutent au total de votre récapitulatif.
            </p>
          </div>

          {/*
          00249 — sélection figée. On le dit AVANT que le participant ne coche
          quoi que ce soit : le socle refuserait le changement à l'envoi, et le
          dossier continuerait de facturer la sélection d'origine.

          Pas de `role="status"` (revue adversariale 09/2026) : la bannière est
          présente dès le premier rendu, une région live n'annoncerait donc
          rien. C'est chaque case verrouillée qui la référence en
          `aria-describedby` — le lecteur d'écran énonce la raison au focus.
        */}
          {(optionsLocked || engagedOptionIds !== null) && (
            <p
              className="flex items-start gap-1.5 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800"
              id="bed-options-locked-note"
              data-testid="bed-options-locked"
            >
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {optionsLocked
                ? 'Votre paiement a commencé : votre sélection d’options ne peut plus être modifiée.'
                : 'Votre paiement est déjà engagé : vous ne pouvez plus ajouter d’option en ligne.'}
            </p>
          )}

          {optionalItems.length === 0 ? (
            <p className="rounded-lg border border-dashed border-zinc-200 p-4 text-sm text-zinc-500">
              Aucune option n’est proposée pour cette édition.
            </p>
          ) : (
            <ul className="space-y-3">
              {optionalItems.map((item) => {
                const acked = isConditionsAcknowledged(item, acknowledged);
                const selected = selectedIds.includes(item.pricing_item_id);
                // Exigence assureur (cahier L91) : on ne peut pas cocher l'option
                // tant que le document n'a pas été acquitté.
                const lockedByAck = Boolean(item.conditions_url) && !acked;
                // 00249 : sélection figée par le paiement en cours. 00280 : au
                // premier envoi sur un dossier engagé, une option du dossier ne se
                // retire pas et aucune autre ne s'ajoute ; une option cochée avant
                // l'engagement reste décochable.
                const lockedByPayment =
                  optionsLocked ||
                  (engagedOptionIds !== null &&
                    (engagedOptionIds.includes(item.pricing_item_id) || !selected));
                const locked = lockedByAck || lockedByPayment;
                const ackHintId = `option-ack-hint-${item.pricing_item_id}`;
                // La case verrouillée reste ATTEIGNABLE au clavier et annonce
                // pourquoi elle ne bouge pas (revue adversariale 09/2026) : elle
                // référence l'indice de certification et/ou la bannière de verrou.
                const lockNoteIds =
                  [
                    lockedByAck ? ackHintId : null,
                    lockedByPayment ? 'bed-options-locked-note' : null,
                  ]
                    .filter(Boolean)
                    .join(' ') || undefined;

                return (
                  <li
                    key={item.pricing_item_id}
                    className={`space-y-3 rounded-lg border p-4 ${
                      selected ? 'border-zinc-900 bg-zinc-50/60' : 'border-zinc-200'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id={`option-${item.pricing_item_id}`}
                        checked={selected}
                        disabled={disabled}
                        aria-disabled={locked || undefined}
                        aria-describedby={lockNoteIds}
                        onChange={(event) => {
                          if (locked) return;
                          onToggleOption(item, event.target.checked);
                        }}
                        className={`mt-0.5 h-4 w-4 shrink-0 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900 disabled:opacity-50 ${
                          locked ? 'cursor-not-allowed opacity-50' : ''
                        }`}
                      />
                      <div className="min-w-0 flex-1">
                        <label
                          htmlFor={`option-${item.pricing_item_id}`}
                          className={`flex flex-wrap items-baseline justify-between gap-2 text-sm ${
                            locked ? 'cursor-not-allowed' : 'cursor-pointer'
                          }`}
                        >
                          <span className="font-medium text-zinc-900">{item.label}</span>
                          <span className="font-semibold text-zinc-900">
                            {formatCents(item.amount_cents)}
                          </span>
                        </label>

                        {/*
                        Indice de certification : affiché MÊME quand la sélection
                        est figée par le paiement (revue adversariale 09/2026).
                        Le masquer laissait le participant devant une option
                        décertifiée — document remplacé entre deux soumissions,
                        ou case décochée — sans savoir que re-cocher « J'ai pris
                        connaissance » débloque l'envoi.
                      */}
                        {lockedByAck && (
                          <p
                            id={ackHintId}
                            className="mt-1 flex items-center gap-1.5 text-xs font-medium text-amber-700"
                          >
                            <Lock className="h-3.5 w-3.5" />
                            {selected
                              ? 'Cochez « J’ai pris connaissance du document » ci-dessous : sans cette confirmation, votre bulletin ne peut pas être envoyé.'
                              : 'Prenez connaissance du document avant de pouvoir cocher cette option.'}
                          </p>
                        )}
                      </div>
                    </div>

                    {item.conditions_url && (
                      <div className="space-y-2 rounded-md bg-white p-3 ring-1 ring-zinc-100">
                        <ConditionsLink url={item.conditions_url} />
                        <div className="flex items-start gap-2">
                          <input
                            type="checkbox"
                            id={`ack-${item.pricing_item_id}`}
                            checked={acked}
                            disabled={disabled}
                            onChange={(event) => onToggleAck(item, event.target.checked)}
                            className="mt-0.5 h-4 w-4 shrink-0 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900"
                          />
                          <label
                            htmlFor={`ack-${item.pricing_item_id}`}
                            className="cursor-pointer text-sm text-zinc-700"
                          >
                            J’ai pris connaissance du document
                            <span className="ml-1 text-red-500">*</span>
                          </label>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {/* ---------------- Chambre double Marrakech ---------------- */}
      <section className="space-y-3">
        <h3 className="text-base font-semibold text-zinc-900">Hébergement à Marrakech</h3>
        <div className="rounded-lg border border-zinc-200 p-4 space-y-3">
          <p className="text-sm text-zinc-700">
            Souhaiteriez-vous bénéficier d'une chambre double (un grand lit) lors de vos deux nuits
            à Marrakech ? <span className="text-zinc-500">Sous réserve de disponibilité.</span>
            <span className="ml-1 text-red-500">*</span>
          </p>
          <div className="flex gap-6">
            {(['oui', 'non'] as const).map((val) => (
              <label
                key={val}
                className="flex items-center gap-2 cursor-pointer text-sm text-zinc-700"
              >
                <input
                  type="radio"
                  name="double_room"
                  value={val}
                  checked={doubleRoomAnswer === val}
                  disabled={disabled}
                  onChange={() => onDoubleRoomChange?.(val)}
                  className="h-4 w-4 border-zinc-300 text-zinc-900 focus:ring-zinc-900"
                />
                {val.charAt(0).toUpperCase() + val.slice(1)}
              </label>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- Consentements ---------------- */}
      {consentFields.length > 0 && (
        <section className="space-y-3">
          <h3 className="text-base font-semibold text-zinc-900">Consentements</h3>
          <div className="space-y-1 rounded-lg border border-zinc-200 p-4">
            {consentFields.map((field) => (
              <BedFieldInput
                key={field.id}
                field={field}
                values={values}
                required={isRequired(field)}
                disabled={disabled}
                onChange={onChangeField}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
